import React from 'react';
import { motion } from 'framer-motion';
import type { Slide } from './slides';
const ease = [0.22, 1, 0.36, 1] as const;
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease
    }
  }
};
function Eyebrow({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const Icon = slide.icon;
  return <motion.div variants={item} className="flex items-center gap-3 text-accent" data-id="element-92" data-mp="2orgbksviwjzb">
      <span className="flex h-8 w-8 items-center justify-center rounded-md border border-accent/25 bg-accent/10" data-id="element-93" data-mp="2ko0fpyra2nno">
        <Icon className="h-4 w-4" strokeWidth={2.2} data-id="element-94" data-mp="2fonjwoifi68y" />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.25em]" data-id="element-95" data-mp="199d5dgaouj6p">
        {String(index + 1).padStart(2, '0')} · {slide.section}
      </span>
    </motion.div>;
}
export function SlideView({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const isTitle = slide.kind === 'title';
  return <motion.div key={slide.id} variants={container} initial="hidden" animate="show" exit={{
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.25,
      ease
    }
  }} className="mx-auto flex w-full max-w-5xl flex-col justify-center px-6 py-16 sm:px-10 md:px-14" data-id="element-96" data-mp="1n4h4vc4mdvq8">
      {!isTitle && !slide.positioningPoints && !slide.competitiveTableRows && <Eyebrow slide={slide} index={index} data-id="element-97" data-mp="zdtwyqupf63o" />}

      <div className={`${slide.positioningPoints || slide.competitiveTableRows ? '' : isTitle ? 'mt-3' : 'mt-8'} ${slide.portrait ? 'grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_270px]' : ''}`} data-id="element-98" data-mp="146eotmu1v94k">
        <div data-id="element-99" data-mp="ufjx42y1bhbq">
          {isTitle && <motion.div variants={item} className="flex items-center gap-5 sm:gap-6" data-id="element-100" data-mp="ugblaspaycrz">
              <img src="https://cdn.magicpatterns.com/uploads/cviN53Xkz9VGisvd9pR2jC/openfpga_logo.jpg" alt="Isochron Systems logo" className="h-20 w-20 rounded-2xl object-cover sm:h-24 sm:w-24" data-id="element-101" data-mp="ghlqjjpygp6s" />
              <div className="text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-102" data-mp="2ie2tl1apatfw">
                Isochron <span className="text-accent" data-id="element-103" data-mp="2ebg2x3prneka">Systems</span>
              </div>
            </motion.div>}

          {!slide.moatStages && !slide.velocityCards && !slide.positioningPoints && !slide.competitiveTableRows && <motion.h1 variants={item} className={`font-extrabold tracking-tight text-white ${slide.id === 'title' ? 'mt-16 text-3xl leading-[1.15] sm:mt-20 sm:text-4xl lg:text-5xl' : isTitle ? 'text-5xl leading-[1.05] sm:text-6xl md:text-7xl' : 'text-4xl leading-[1.08] sm:text-5xl md:text-6xl'}`} data-id="element-104" data-mp="362m5rrwvfqd9">
              {slide.title}
            </motion.h1>}

          {slide.lead && <motion.p variants={item} className="mt-6 max-w-3xl text-lg font-medium text-accent/90 sm:text-xl" data-id="element-105" data-mp="1goh0sdgb1ef0">
              {slide.lead}
            </motion.p>}

          {slide.body.length > 0 && <motion.div variants={item} className="mt-6 max-w-3xl space-y-4 text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-106" data-mp="1bk52zejdt03j">
              {slide.body.map((p, i) => <p key={i} data-id="element-107" data-mp="1lareawk2f4gf" data-mpi={i}>{p}</p>)}
            </motion.div>}

          {slide.metrics && !slide.competitiveTableRows && <motion.div variants={item} className={`overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.06] ${slide.kind === 'appendix' ? 'mt-10 grid max-w-3xl grid-cols-1 gap-px sm:grid-cols-2' : 'mt-12 grid grid-cols-2 gap-px sm:grid-cols-4'}`} data-id="element-108" data-mp="1u9lf6z4figzs">
              {slide.metrics.map(m => <div key={m.label} className={`bg-panel2 ${slide.kind === 'appendix' ? 'px-5 py-5 sm:px-6' : 'px-5 py-7 sm:px-6 sm:py-8'}`} data-id="element-109" data-mp="9vb1vqfgoynn">
                  {slide.kind === 'appendix' ? <>
                      <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent" data-id="element-110" data-mp="29as46nyl0d96">
                        {m.label}
                      </div>
                      {m.href ? <a href={m.href} target={m.href.startsWith('http') ? '_blank' : undefined} rel={m.href.startsWith('http') ? 'noreferrer' : undefined} className="mt-2 block break-words text-base font-semibold leading-snug text-white underline decoration-accent/50 underline-offset-4 transition-colors hover:text-accent hover:decoration-accent sm:text-lg" data-id="element-111" data-mp="1go7cfme71z53">
                          {m.value}
                        </a> : <div className="mt-2 break-words text-base font-semibold leading-snug text-white sm:text-lg" data-id="element-112" data-mp="34k406hs8gppg">
                          {m.value}
                        </div>}
                    </> : <>
                      <div className="font-mono text-2xl font-semibold text-accent sm:text-3xl" data-id="element-113" data-mp="30j1yzvera3ku">
                        {m.value}
                      </div>
                      <div className="mt-2 text-sm leading-snug text-slate-400" data-id="element-114" data-mp="3o71oyt48tpyd">
                        {m.label}
                      </div>
                    </>}
                </div>)}
            </motion.div>}

          {slide.mechanisms && <motion.div variants={item} className="mt-9 grid gap-3 md:grid-cols-3" aria-label="Defensible serving mechanisms" data-id="element-115" data-mp="3j88b2zl1nwxo">
              {slide.mechanisms.map((mechanism, mechanismIndex) => <article key={mechanism.title} className="rounded-lg border border-white/[0.08] bg-panel2 p-4" data-id="element-116" data-mp="1ntbd0ho7b61g" data-mpi={mechanismIndex}>
                  <span className="font-mono text-xs font-semibold text-accent" data-id="element-117" data-mp="1jtkefrsfnlmd">
                    0{mechanismIndex + 1}
                  </span>
                  <h2 className="mt-2 text-sm font-bold text-white" data-id="element-118" data-mp="1a0bxwgsbzduf">
                    {mechanism.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400" data-id="element-119" data-mp="153lhttgj0i90">
                    {mechanism.body}
                  </p>
                </article>)}
            </motion.div>}

          {slide.moatStages && <motion.section variants={item} className="mt-1" aria-label="Isochron model and silicon pipeline" data-id="element-120" data-mp="r32egzn0a0d6">
              <h1 className="max-w-6xl text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl" data-id="element-121" data-mp="2qelkb5d0isim">
                {slide.moatHeadline}
              </h1>
              {slide.moatDescription && <p className="mt-4 max-w-5xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-122" data-mp="2szt21pa11jkl">
                  {slide.moatDescription}
                </p>}

              <div className="mt-6 grid gap-2 lg:grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)]" data-id="element-123" data-mp="32r74a4td4xhf">
                {slide.moatStages.map((stage, stageIndex) => <React.Fragment key={stage.number} data-id="element-124" data-mp="2a2ibqu6di0ha" data-mpi={stageIndex}>
                    <article className={`min-h-[150px] rounded-xl border bg-panel2 p-4 ${stageIndex === slide.moatStages!.length - 1 ? 'border-accent/80 bg-accent/[0.06]' : 'border-slate-700/80'}`} data-id="element-125" data-mp="2f01r5xet4jat">
                      <div className="font-mono text-[11px] font-medium tracking-[0.14em] text-accent" data-id="element-126" data-mp="2o0q6cvfxsppa">{stage.number} · {stage.title}</div>
                      <h2 className="mt-3 text-lg font-bold leading-tight text-slate-100 sm:text-xl" data-id="element-127" data-mp="3vdrvn59u99kz">{stage.detail.split('\n')[0]}</h2>
                      {stage.detail.split('\n')[1] && <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base" data-id="element-128" data-mp="pdcetr5376mp">{stage.detail.split('\n')[1]}</p>}
                    </article>
                    {stageIndex < slide.moatStages!.length - 1 && <span aria-hidden className="hidden self-center text-center text-lg font-semibold text-accent lg:block" data-id="element-129" data-mp="z3x7yz1zriy8">→</span>}
                  </React.Fragment>)}
              </div>

              {slide.moatStats && <div className="mt-5 flex flex-wrap gap-2 font-mono text-xs leading-relaxed text-slate-200 sm:text-sm" data-id="element-130" data-mp="13362i64uvkuu">
                  {slide.moatStats.map((stat, statIndex) => <span key={stat.value} className="rounded-full border border-slate-700 bg-panel2 px-3 py-2" data-id="element-131" data-mp="1d36ra7lhut6f" data-mpi={statIndex}>
                      <span className={statIndex === 2 ? 'text-slate-200' : 'font-semibold text-accent'} data-id="element-132" data-mp="3jsv2ixejstfy">{stat.value}</span>{' '}
                      <span className={statIndex === 2 || statIndex === 3 ? 'text-accent font-semibold' : ''} data-id="element-133" data-mp="3evl7n28e3pkq">{stat.label}</span>
                    </span>)}
                </div>}

              {slide.moatConclusion && <p className="mt-5 rounded-xl border border-slate-700 border-l-4 border-l-accent bg-panel2 px-5 py-5 text-lg font-bold leading-snug text-slate-100 sm:text-xl" data-id="element-134" data-mp="1kkle6i32yrpa">
                  {slide.moatConclusion}
                </p>}
            </motion.section>}

          {slide.positioningPoints && <motion.figure variants={item} className="mt-1" aria-labelledby="positioning-map-title" data-id="element-135" data-mp="1fk75cxtnndq7">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-accent" data-id="element-136" data-mp="11omumpo2y1tv">
                {String(index + 1).padStart(2, '0')} · Appendix
              </p>
              <h1 id="positioning-map-title" className="mt-4 text-3xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl" data-id="element-137" data-mp="2w1cqve1x7n4f">
                {slide.title}
              </h1>

              <div className="mt-7 overflow-x-auto pb-12 pl-28" data-id="element-138" data-mp="2zi03p230lnru">
                <div className="relative min-w-[680px] border-b-2 border-l-2 border-slate-600" style={{
              height: '430px'
            }} data-id="element-139" data-mp="34b1k5eivdvdj">
                  <div className="absolute left-1/2 top-0 h-full border-l border-dashed border-slate-600/70" data-id="element-140" data-mp="2qk651khs2i1i" />
                  <div className="absolute left-0 top-1/2 w-full border-t border-dashed border-slate-600/70" data-id="element-141" data-mp="2vfljoenm0hyz" />
                  <div className="absolute right-[2%] top-[4%] h-[43%] w-[46%] rounded-2xl border-2 border-dashed border-accent/70" data-id="element-142" data-mp="3j5fw4v40o4ip" />

                  <div className="absolute -left-[96px] top-0 text-right" data-id="element-143" data-mp="399mcysvslcry">
                    <p className="text-base font-bold text-white" data-id="element-144" data-mp="3e879ah7xbcb4">Lockstep</p>
                    <p className="mt-1 text-xs text-slate-400" data-id="element-145" data-mp="3eaa9tv6pcnu3">p99 = p50</p>
                  </div>
                  <p className="absolute -bottom-8 left-0 text-sm font-bold text-white" data-id="element-146" data-mp="1xw0o78tuhd6y">Frozen silicon</p>
                  <p className="absolute -bottom-8 right-0 text-sm font-bold text-white" data-id="element-147" data-mp="22qo1scnb8n6r">Reconfigurable datapath</p>
                  <p className="absolute -left-[96px] bottom-2 text-base font-bold text-white" data-id="element-148" data-mp="1qaeuivs3ys1o">Best-effort</p>

                  {slide.positioningPoints.map(point => <div key={point.name} className={`absolute -translate-x-1/2 -translate-y-1/2 ${point.featured ? 'z-10' : ''}`} style={{
                left: `${point.x}%`,
                top: `${point.y}%`
              }} data-id="element-149" data-mp="2483i4bdk8daf">
                      <span className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full ${point.featured ? 'bg-accent ring-4 ring-accent/15' : 'bg-slate-500'}`} data-id="element-150" data-mp="17l8930zxjmc6" />
                      <div className={`w-40 ${point.x > 60 ? '-translate-x-[calc(100%+1.25rem)] text-right' : 'translate-x-6 text-left'}`} data-id="element-151" data-mp="1gk3gggepn2h2">
                        <p className={`text-sm font-bold ${point.featured ? 'text-accent' : 'text-white'}`} data-id="element-152" data-mp="stg49ixrhqop">{point.name}</p>
                        <p className={`mt-0.5 whitespace-pre-line text-xs leading-snug ${point.featured ? 'text-accent/80' : 'text-slate-400'}`} data-id="element-153" data-mp="nu3kj726szqc">{point.description}</p>
                      </div>
                    </div>)}
                </div>
              </div>

              <figcaption className="mt-1 max-w-4xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-154" data-mp="2xoschs0f5qu6">
                Hardware flexibility: hours + $0 NRE vs years + $50-150M respin
              </figcaption>

              <ul className="sr-only" data-id="element-155" data-mp="251o4rf9xyyep">
                {slide.positioningPoints.map(point => <li key={point.name} data-id="element-156" data-mp="29yoblsem856v">{point.name}: {point.description}</li>)}
              </ul>
            </motion.figure>}

          {slide.velocityCards && <motion.section variants={item} className="mt-1" aria-label="Thirty-day velocity" data-id="element-157" data-mp="2j05mmwd61gop">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-accent" data-id="element-158" data-mp="3qbmkhpg1uxqr">
                {slide.velocityHeadline}
              </p>
              <h1 className="mt-3 text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl" data-id="element-159" data-mp="1iqwivy7sbnb1">
                30-Day Velocity
              </h1>
              {slide.velocityDescription && <p className="mt-4 max-w-5xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-160" data-mp="19y2r51b5ivga">
                  {slide.velocityDescription}
                </p>}

              <div className="mt-6 grid gap-2 md:grid-cols-2 lg:grid-cols-4" data-id="element-161" data-mp="1006fm8eaxyhn">
                {slide.velocityCards.map(card => <article key={card.value} className={`min-h-[126px] rounded-xl border border-slate-700/80 bg-panel2 p-3 ${card.className ?? ''}`} data-id="element-162" data-mp="v0a83z6eboxc">
                    <p className="font-mono text-2xl font-semibold tracking-tight text-accent sm:text-3xl" data-id="element-163" data-mp="m1yl074qn99n">
                      {card.value}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400 sm:text-sm" data-id="element-164" data-mp="2ge503h0r6a78">{card.detail}</p>
                  </article>)}
              </div>

              {slide.velocityChips && <div className="mt-4 flex flex-wrap gap-2 font-mono text-xs leading-relaxed sm:text-sm" data-id="element-165" data-mp="1i33o53sozbkp">
                  {slide.velocityChips.map(chip => <span key={chip.prefix} className="rounded-xl border border-slate-700 bg-panel2 px-3 py-2 text-slate-400" data-id="element-166" data-mp="1925kwnq2v836">
                      <span className="font-semibold text-accent" data-id="element-167" data-mp="ud10r0zq2tsf">{chip.prefix}</span>{chip.suffix}
                    </span>)}
                </div>}
            </motion.section>}

          {slide.competitiveTableRows && <motion.section variants={item} className="mt-1" aria-label="Competitive positioning" data-id="element-168" data-mp="laruinbhlmku">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-accent" data-id="element-169" data-mp="beeznm47h2oi">
                {String(index + 1).padStart(2, '0')} · Appendix
              </p>
              <h1 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl md:text-6xl" data-id="element-170" data-mp="gfksrl1s2ryk">
                {slide.title}
              </h1>
              <div className="mt-7 overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-171" data-mp="3nzavduip4eat">
                <table className="w-full min-w-[760px] border-collapse text-left" data-id="element-172" data-mp="6qnnijvn8czj">
                  <thead className="bg-panel" data-id="element-173" data-mp="1qjetg0z8mjv">
                    <tr className="border-b border-white/[0.08]" data-id="element-174" data-mp="1svybxmdv4z1">
                      {['Approach', 'Decode efficiency', 'Deterministic latency', 'New model onto the chip'].map(heading => <th key={heading} scope="col" className="px-4 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400" data-id="element-175" data-mp="apeoj238cerx">
                          {heading}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-176" data-mp="uhb5xz36ql0c">
                    {slide.competitiveTableRows.map(row => <tr key={row.approach} className={row.approach === 'Isochron' ? 'bg-accent/[0.06]' : ''} data-id="element-177" data-mp="16h85zflss4w8">
                        <td className="px-4 py-3 align-top" data-id="element-178" data-mp="1bgkq3slaaxv4">
                          <div className={`text-sm font-semibold ${row.approach === 'Isochron' ? 'text-accent' : 'text-white'}`} data-id="element-179" data-mp="1g7koz3dyrq09">{row.approach}</div>
                          <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-180" data-mp="1k8cyzyuntyxl">{row.approachDetail}</div>
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-181" data-mp="1pa2crm99oqpd">
                          <div className="text-sm font-medium text-slate-200" data-id="element-182" data-mp="1u8nc7jgp36cr">{row.decodeEfficiency}</div>
                          {row.decodeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-183" data-mp="1z4cg1yzqz9ev">{row.decodeDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-184" data-mp="4rw0u8oz4kxv">
                          <div className="text-sm font-medium text-slate-200" data-id="element-185" data-mp="4ui9kbnlp6ho">{row.deterministicLatency}</div>
                          {row.deterministicDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-186" data-mp="2dbfe01k3ix2o">{row.deterministicDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-187" data-mp="29als1dq7rscf">
                          <div className="text-sm font-medium text-slate-200" data-id="element-188" data-mp="24c25pc7pkejj">{row.modelChange}</div>
                          {row.modelChangeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-189" data-mp="31100gufgkcgp">{row.modelChangeDetail}</div>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <p className="mt-5 text-lg font-medium leading-relaxed text-accent sm:text-xl" data-id="element-190" data-mp="2n2a27bgwk6qr">
                The only deterministic inference that isn't frozen into silicon.
              </p>

              {slide.proof && <p className="mt-5 max-w-full overflow-x-auto whitespace-nowrap border-t border-white/[0.08] pt-4 text-base font-medium text-slate-300 sm:text-lg" data-id="element-191" data-mp="1bltvq7nbt0g1">
                  {slide.proof}
                </p>}
            </motion.section>}

          {slide.comparisonRows && <motion.div variants={item} className="mt-8 overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-192" data-mp="31s85xndwor8z">
              <table className="w-full min-w-[640px] border-collapse text-left" aria-label="GPU-serving pain points and FPGA answers" data-id="element-193" data-mp="3ufm4fpx95uv7">
                <thead className="bg-panel" data-id="element-194" data-mp="37q7o0u6z54f">
                  <tr className="border-b border-white/[0.08]" data-id="element-195" data-mp="3kpj5l93g9moy">
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400" data-id="element-196" data-mp="3kozv2dg4q0ky">
                      GPU-serving pain
                    </th>
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent" data-id="element-197" data-mp="h7hsakf7rjre">
                      Our FPGA answer
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-198" data-mp="21rd41x3gqiob">
                  {slide.comparisonRows.map(row => <tr key={row.pain} data-id="element-199" data-mp="2geg3waf9d3et">
                      <td className="px-5 py-3.5 text-sm leading-relaxed text-slate-300" data-id="element-200" data-mp="2ghkgnw6sh7h3">
                        {row.pain}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-medium leading-relaxed text-accent/90" data-id="element-201" data-mp="1nrv9t27eed7k">
                        {row.answer}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </motion.div>}

          {slide.footer && !slide.moatStages && !slide.competitiveTableRows && <motion.p variants={item} className="mt-6 border-t border-white/[0.08] pt-4 font-mono text-[11px] leading-relaxed text-slate-500" data-id="element-202" data-mp="386e0e3weh2vf">
              {slide.footer}
            </motion.p>}
        </div>

        {slide.portrait && <motion.figure variants={item} className="mx-auto mt-2 w-full max-w-[270px] lg:mx-0" data-id="element-203" data-mp="2ox4tgygu01hq">
            <div className="overflow-hidden rounded-xl border border-accent/25 bg-panel2 p-1.5 shadow-2xl shadow-black/30" data-id="element-204" data-mp="32wcvs08ajpq5">
              <img src={slide.portrait.src} alt={slide.portrait.alt} className="aspect-[4/5] w-full rounded-lg object-cover object-center" data-id="element-205" data-mp="2xwi1rnt7yk0v" />
            </div>
            <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-500" data-id="element-206" data-mp="37v7kfisxw8i6">
              Orion Parrott · Founder
            </figcaption>
          </motion.figure>}
      </div>
    </motion.div>;
}